import React from "react";

const SEED_TO_SWATCH_CLUB = {
  "Brisbane Broncos": "Brisbane Broncos",
  "Canberra Raiders": "Canberra Raiders",
  "Canterbury-Bankstown Bulldogs": "Canterbury Bulldogs",
  "Cronulla-Sutherland Sharks": "Cronulla Sharks",
  "Gold Coast Titans": "Gold Coast Titans",
  "Manly-Warringah Sea Eagles": "Manly Warringah Sea Eagles",
  "Melbourne Storm": "Melbourne Storm",
  "Newcastle Knights": "Newcastle Knights",
  "North Queensland Cowboys": "North Queensland Cowboys",
  "Parramatta Eels": "Parramatta Eels",
  "Penrith Panthers": "Penrith Panthers",
  "South Sydney Rabbitohs": "South Sydney Rabbitohs",
  "St. George Illawarra Dragons": "St George Illawarra Dragons",
  "Sydney Roosters": "Sydney Roosters",
  "Wests Tigers": "Wests Tigers",
  "New Zealand Warriors": "New Zealand Warriors",
  Dolphins: "Redcliffe Dolphins",
};

// The 17 real clubs whose committed marks live under /team-marks/. Any other
// name is treated as unknown and degrades to a neutral initials swatch below.
const KNOWN_TEAMS = new Set(Object.keys(SEED_TO_SWATCH_CLUB));

// R14: State of Origin state marks. "New South Wales" and "Queensland" are the
// exact home_ref/away_ref spellings on Origin fixtures. They render their
// committed marks (no initials fallback) but are NOT clubs: they are not in
// SEED_TO_SWATCH_CLUB, not exported in any team list, and never appear in a
// picker, favourite-team or squad screen (those read the `teams` table, which
// has no state rows). Unknown names keep the initials fallback.
const STATE_MARKS = new Set(["New South Wales", "Queensland"]);
for (const state of STATE_MARKS) KNOWN_TEAMS.add(state);

// Neutral surface/border design tokens. These mirror App.jsx's `T` tokens
// (surface #ffffff, border rgba(0,0,0,0.08)), which are not exported from there.
// They are app chrome tokens — NOT club colours (RUL-2/IP) — so re-declaring the
// same neutral values here keeps the fallback off any club identity palette.
const FALLBACK_SURFACE = "#ffffff";
const FALLBACK_BORDER = "rgba(0,0,0,0.08)";

function slugify(teamName) {
  return teamName.toLowerCase().replaceAll(" ", "-");
}

export function teamMarkUrl(seedTeamName) {
  return `/team-marks/${slugify(SEED_TO_SWATCH_CLUB[seedTeamName] ?? seedTeamName)}.png`;
}

// First two initials of a team name, for the neutral fallback swatch. A single
// word yields its first two characters; multiple words yield the first letter
// of each of the first two words.
export function initialsFor(teamName) {
  const words = String(teamName == null ? "" : teamName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

function fallbackSwatchStyle(size, style) {
  const sizeStyle = size === undefined ? undefined : { width: size, height: size };
  return {
    ...sizeStyle,
    display: "none",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 4,
    background: FALLBACK_SURFACE,
    border: `1px solid ${FALLBACK_BORDER}`,
    fontWeight: 700,
    lineHeight: 1,
    ...style,
  };
}

export function TeamMark({ team, size, className, style, onError, ...props }) {
  const sizeStyle = size === undefined ? undefined : { width: size, height: size };

  // Known club: render the real mark exactly as before (byte-identical output).
  if (KNOWN_TEAMS.has(team)) {
    return React.createElement("img", {
      src: teamMarkUrl(team),
      alt: "",
      "aria-hidden": "true",
      className,
      style: { ...sizeStyle, ...style },
      onError,
      ...props,
    });
  }

  // Unknown name: render the mark <img> with an onError handler that hides the
  // broken image and reveals an adjacent neutral initials swatch. The fallback
  // is rendered alongside, hidden by default, so server-side rendering
  // (renderToStaticMarkup, which strips client event handlers) still emits
  // sensible markup when the mark file is simply absent at runtime — the
  // onError path is purely a client-runtime enhancement.
  //
  // The internal handler COMPOSES any caller-supplied onError rather than
  // being overridden by it: `...props` is spread first and `onError` is
  // pinned last below, so a caller's onError can never replace the fallback
  // hide/reveal wiring (round-3 review: previously `...props` followed
  // onError, letting a caller onError clobber the swap). The caller's
  // onError still runs — after the swap — for their own logging/telemetry.
  const revealFallback = (event) => {
    const img = event.currentTarget;
    if (img && img.style) img.style.display = "none";
    const next = img && img.nextElementSibling;
    if (next && next.style) next.style.display = "inline-flex";
    if (typeof onError === "function") onError(event);
  };

  return React.createElement(
    "span",
    { style: { display: "inline-flex", alignItems: "center", verticalAlign: "middle" } },
    React.createElement("img", {
      src: teamMarkUrl(team),
      alt: "",
      "aria-hidden": "true",
      className,
      style: { ...sizeStyle, ...style },
      ...props,
      onError: revealFallback,
    }),
    React.createElement("span", {
      "aria-hidden": "true",
      className,
      style: fallbackSwatchStyle(size, style),
    }, initialsFor(team)),
  );
}

export function TeamName({ team, size = 14, prominent = false, style, children }) {
  return React.createElement(
    "span",
    {
      style: {
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
        ...style,
      },
    },
    React.createElement(TeamMark, {
      team,
      size,
      style: {
        borderRadius: prominent ? 6 : 3,
        marginRight: prominent ? 8 : 4,
        flexShrink: 0,
      },
    }),
    children ?? team,
  );
}

export function TeamMatchup({ home, away, size = 14, prominent = false, style }) {
  return React.createElement(
    "span",
    { style },
    React.createElement(TeamName, { team: home, size, prominent }),
    " vs ",
    React.createElement(TeamName, { team: away, size, prominent }),
  );
}
